'use client';

import { EditorContent, type Extension } from '@tiptap/react';
import { ChevronLeft, ChevronRight, FileAudio, FileText, ImageIcon, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MentionContext } from '@/app/components/shared/MentionsTextarea/context';
import { SuggestionPluginKey } from '@/app/components/shared/MentionsTextarea/extensions/custom-suggestion';
import { useCustomMention } from '@/app/components/shared/MentionsTextarea/hooks/use-custom-mention';
import { useEditorWithExtensions } from '@/app/hooks/editor';
import { useStore } from '@/app/providers/store';
import type { MentionItem, TextPromptWithInputItem } from '@/app/types';
import { cn, extractMentionsIdsFromEditor } from '@/app/utils';
import { useClawMentionSearch } from '../../_hook/use-claw-mention-search';
import { getMediaItemType, shouldDisplayMediaFile } from '../../_utils';
import { getDownloadUrl } from '../../apis';
import type { ClawFileItem, StoryAsset, StoryShot } from '../../types';
import { getItemName, getPreviewTypeFromPath } from '../File/utils';
import { ShotAssetChip } from './ShotAssetChip';

const DURATION_MIN = 1;
const DURATION_MAX = 120;
const PROMPT_MENTION_FILE_RE =
  /@([^\s@【】[\]（）()，。；：、,;!?！？"'“”‘’]+?\.(?:png|jpg|jpeg|webp|gif|bmp|mp4|mov|webm|mkv|mp3|wav|m4a|aac|flac|ogg|md|markdown|pdf|csv|txt|json|yaml|yml|xml|ppt|pptx))/gi;

interface StoryShotPaneProps {
  shots: StoryShot[];
  activeShotIndex: number;
  activeShot: StoryShot;
  files: ClawFileItem[];
  onPrev: () => void;
  onNext: () => void;
  onSelectShot: (index: number) => void;
  onUpdateShot: (shotId: string, patch: Partial<Pick<StoryShot, 'prompt' | 'durationSec'>>) => void;
  onAddAsset: (shotId: string, asset: StoryAsset) => void;
  onRemoveAsset: (shotId: string, assetPath: string) => void;
  readOnly?: boolean;
  className?: string;
}

const MENTION_COLOR = '#508DFF';

const buildMentionPrefix = (asset: StoryAsset) => {
  if (asset.previewUrl && (asset.type === 'image' || asset.type === 'video')) {
    return (
      <span className="mr-1 flex size-5 overflow-hidden rounded-full bg-black/20 ring-1 ring-white/20">
        {asset.type === 'image' ? (
          <img src={asset.previewUrl} alt={asset.name} className="size-full object-cover" />
        ) : (
          <video src={asset.previewUrl} className="size-full object-cover" muted playsInline preload="metadata" />
        )}
      </span>
    );
  }

  let Icon = FileText;
  if (asset.type === 'image') Icon = ImageIcon;
  else if (asset.type === 'video') Icon = Video;
  else if (asset.type === 'audio') Icon = FileAudio;

  return (
    <span className="mr-1 inline-flex size-5 items-center justify-center rounded-full bg-white/15 text-[#9EC1FF]">
      <Icon className="size-3.5" />
    </span>
  );
};

const fileItemToStoryAsset = (file: ClawFileItem): StoryAsset => {
  const type = getMediaItemType(file);
  return {
    path: file.path ?? '',
    name: getItemName(file),
    type,
  };
};

const getAssetNameFromPath = (path: string) => path.split('/').filter(Boolean).pop() || path;

const inferStoryAssetTypeFromPath = (pathOrName: string): StoryAsset['type'] => {
  const previewType = getPreviewTypeFromPath(pathOrName);
  if (previewType === 'image' || previewType === 'video' || previewType === 'audio') return previewType;
  return 'file';
};

const dedupeStoryAssets = (assets: StoryAsset[]): StoryAsset[] => {
  const map = new Map<string, StoryAsset>();
  assets.forEach((asset) => {
    if (!asset.path || map.has(asset.path)) return;
    map.set(asset.path, asset);
  });
  return [...map.values()];
};

const collectPromptReferencedAssets = (prompt: string, knownAssets: StoryAsset[]): StoryAsset[] => {
  if (!prompt) return [];

  const knownAssetMap = new Map<string, StoryAsset>();
  knownAssets.forEach((asset) => {
    if (!asset.path) return;
    [asset.path, asset.name, getAssetNameFromPath(asset.path)].forEach((key) => {
      if (key) knownAssetMap.set(key.toLowerCase(), asset);
    });
  });

  const assets: StoryAsset[] = [];
  PROMPT_MENTION_FILE_RE.lastIndex = 0;
  let match = PROMPT_MENTION_FILE_RE.exec(prompt);
  while (match) {
    const token = match[1]?.trim();
    const matchedAsset = token ? knownAssetMap.get(token.toLowerCase()) : undefined;
    const path = matchedAsset?.path || (token?.includes('/') ? token : '');
    if (path) {
      assets.push({
        path,
        name: matchedAsset?.name || getAssetNameFromPath(path),
        type: matchedAsset?.type || inferStoryAssetTypeFromPath(path),
        previewUrl: matchedAsset?.previewUrl,
      });
    }
    match = PROMPT_MENTION_FILE_RE.exec(prompt);
  }

  return dedupeStoryAssets(assets);
};

const stringifyInputValue = (value: TextPromptWithInputItem): string => {
  return value
    .map((item) => {
      if (item.type === 'text') return item.text;
      if (item.type === 'mention') return `@${item.mention.name || item.mention.id}`;
      return '';
    })
    .join('');
};

const parsePromptToInputValue = (prompt: string, references: StoryAsset[]): TextPromptWithInputItem => {
  if (!prompt) return [];
  if (!references.length) return [{ type: 'text', text: prompt }];

  const sortedReferences = [...references]
    .filter((asset) => !!asset.path && !!asset.name)
    .sort((a, b) => b.name.length - a.name.length);

  const result: TextPromptWithInputItem = [];
  let buffer = '';
  let index = 0;

  const flushBuffer = () => {
    if (!buffer) return;
    result.push({ type: 'text', text: buffer });
    buffer = '';
  };

  while (index < prompt.length) {
    if (prompt[index] === '@') {
      const sliced = prompt.slice(index + 1);
      let matchedAsset: StoryAsset | undefined;
      let matchedLength = 0;

      for (const asset of sortedReferences) {
        // 与 story-payload.ts 的 collectMentionedAssetPaths 一致：候选包括
        // 完整 path（@media/sess-xxx/foo.png）、name（含/不含扩展名的文件名）、
        // 路径 basename（兜底），按最长匹配优先。
        const candidates = [asset.path, asset.name, asset.path.split('/').filter(Boolean).pop() || ''];
        for (const candidate of candidates) {
          if (!candidate) continue;
          if (sliced.startsWith(candidate) && candidate.length > matchedLength) {
            matchedAsset = asset;
            matchedLength = candidate.length;
          }
        }
      }

      if (matchedAsset) {
        flushBuffer();
        result.push({
          type: 'mention',
          mention: {
            id: matchedAsset.path,
            name: matchedAsset.name,
            color: MENTION_COLOR,
            preview: matchedAsset.previewUrl,
            mediaType: matchedAsset.type,
          },
        });
        index += 1 + matchedLength;
        continue;
      }
    }

    buffer += prompt[index];
    index += 1;
  }

  flushBuffer();
  return result.length > 0 ? result : [{ type: 'text', text: prompt }];
};

interface StoryPromptEditorProps {
  activeShot: StoryShot;
  files: ClawFileItem[];
  assetPreviewUrlMap: Record<string, string>;
  onUpdatePrompt: (prompt: string) => void;
  onAddAsset: (asset: StoryAsset) => void;
  onRemoveAsset: (assetPath: string) => void;
  readOnly?: boolean;
}

const StoryPromptEditor = ({
  activeShot,
  files,
  assetPreviewUrlMap,
  onUpdatePrompt,
  onAddAsset,
  onRemoveAsset,
  readOnly = false,
}: StoryPromptEditorProps) => {
  const t = useTranslations('claw.storyDetail.shotPane');
  const activeClawId = useStore((state) => state.activeClaw?.id);
  const previousMentionsIds = useRef<string[]>([]);
  const currentFolderItems = useMemo(() => {
    return files
      .filter((file) => shouldDisplayMediaFile(file))
      .map(fileItemToStoryAsset)
      .filter((asset) => !!asset.path);
  }, [files]);

  const promptReferencedAssets = useMemo(
    () => collectPromptReferencedAssets(activeShot.prompt, [...activeShot.assets, ...currentFolderItems]),
    [activeShot.assets, activeShot.prompt, currentFolderItems],
  );

  const promptReferenceAssets = useMemo(
    () => dedupeStoryAssets([...activeShot.assets, ...currentFolderItems, ...promptReferencedAssets]),
    [activeShot.assets, currentFolderItems, promptReferencedAssets],
  );

  const [inputValue, setInputValue] = useState<TextPromptWithInputItem>(() =>
    parsePromptToInputValue(activeShot.prompt, promptReferenceAssets),
  );

  // prompt 由 editor 自己 onUpdate 同步上来，外部不需要再回填。
  // 只在切到不同分镜时按当前 prompt + references 重新初始化 editor 内容；
  // 否则 onUpdatePrompt -> activeShot.prompt -> 这里 setInputValue -> useEditorWithExtensions setContent
  // 会形成一个循环，把刚选中的 mention 节点重置掉。
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 见上方注释，仅监听 activeShot.id
  useEffect(() => {
    setInputValue(parsePromptToInputValue(activeShot.prompt, promptReferenceAssets));
    previousMentionsIds.current = [];
  }, [activeShot.id]);

  const assetMap = useMemo(() => {
    const map = new Map<string, StoryAsset>();
    promptReferenceAssets.forEach((asset) => {
      if (asset.path) {
        map.set(asset.path, {
          ...asset,
          previewUrl: asset.previewUrl || assetPreviewUrlMap[asset.path],
        });
      }
    });
    return map;
  }, [assetPreviewUrlMap, promptReferenceAssets]);

  const draftMentionCandidates = useMemo(
    () =>
      dedupeStoryAssets([...activeShot.assets, ...promptReferencedAssets])
        .filter((asset) => !!asset.path && !!asset.name)
        .map((asset) => ({
          path: asset.path,
          label: asset.name,
          type: asset.type,
          previewUrl: asset.previewUrl || assetPreviewUrlMap[asset.path],
        })),
    [activeShot.assets, assetPreviewUrlMap, promptReferencedAssets],
  );

  const folderMentionCandidates = useMemo(
    () =>
      currentFolderItems.map((asset) => ({
        path: asset.path,
        label: asset.name,
        type: asset.type,
        previewUrl: asset.previewUrl || assetPreviewUrlMap[asset.path],
      })),
    [currentFolderItems, assetPreviewUrlMap],
  );

  const { searchMentionItems, getResolvedItem } = useClawMentionSearch({
    activeClawId,
    draftCandidates: draftMentionCandidates,
    folderCandidates: folderMentionCandidates,
  });

  // useEditorWithExtensions 内部 onUpdate 闭包只在首次创建 editor 时 capture，
  // 之后哪怕切换分镜、props 变了，也仍然调用首次捕获的回调。
  // 这里把切换分镜后才会变的依赖（assetMap / getResolvedItem / 父级 onAddAsset 等）
  // 都收到 ref 里，回调用 useCallback `[]` 依赖、内部读 ref 拿最新值，
  // 避免新分镜里添加的 mention 跑到旧分镜上。
  const onAddAssetRef = useRef(onAddAsset);
  const onRemoveAssetRef = useRef(onRemoveAsset);
  const onUpdatePromptRef = useRef(onUpdatePrompt);
  const assetMapRef = useRef(assetMap);
  const getResolvedItemRef = useRef(getResolvedItem);
  onAddAssetRef.current = onAddAsset;
  onRemoveAssetRef.current = onRemoveAsset;
  onUpdatePromptRef.current = onUpdatePrompt;
  assetMapRef.current = assetMap;
  getResolvedItemRef.current = getResolvedItem;

  const handleInputUpdate = useCallback((next: TextPromptWithInputItem) => {
    setInputValue(next);
    onUpdatePromptRef.current(stringifyInputValue(next));
  }, []);

  const handleBeforeAddMention = useCallback((mentionItem: MentionItem) => {
    const resolvedItem = getResolvedItemRef.current(mentionItem.id);
    const asset =
      assetMapRef.current.get(mentionItem.id) ??
      (resolvedItem
        ? {
            path: resolvedItem.path,
            name: resolvedItem.label,
            type: resolvedItem.fileType,
            previewUrl: resolvedItem.preview,
          }
        : undefined);
    if (!asset?.path) return false;
    onAddAssetRef.current(asset);
    return true;
  }, []);

  const handleBeforeDeleteMention = useCallback((mentionIds: string[]) => {
    for (const id of mentionIds) onRemoveAssetRef.current(id);
    return true;
  }, []);

  const { mentionExtension, onUpdateCallback } = useCustomMention({
    previousMentionsIds,
    onBeforeAddMention: handleBeforeAddMention,
    onBeforeDeleteMention: handleBeforeDeleteMention,
    customSearchFn: searchMentionItems,
    enableRemoteSearch: false,
    mentionColor: MENTION_COLOR,
    mentionClassName: 'border border-[#8BB8FF]/35 text-[#3F6BCC] dark:text-[#EAF2FF]',
    mentionVariant: 'media-chip',
  });

  const { editor, isEmpty } = useEditorWithExtensions({
    content: inputValue,
    updateContent: handleInputUpdate,
    extensions: [mentionExtension as Extension],
    editable: !readOnly,
    editorProps: {
      attributes: {
        class:
          'min-h-[132px] w-full border-0 bg-transparent text-sm leading-6 text-system-white outline-none focus-visible:outline-none',
      },
      handleKeyDown: (_view, event) => {
        if (event.isComposing || event.key === 'Process') {
          return true;
        }
        if (event.key !== 'Enter') return false;

        const isPlainEnter = !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;
        if (!isPlainEnter) {
          event.preventDefault();
          event.stopPropagation();
          if (editor && !editor.isDestroyed) {
            editor.chain().focus().splitBlock().run();
          }
          return true;
        }

        const isPhysicalEnter = event.code === 'Enter' || event.code === 'NumpadEnter';
        if (!isPhysicalEnter) {
          event.preventDefault();
          event.stopPropagation();
          return true;
        }

        const mentionSuggestionState = editor ? SuggestionPluginKey.getState(editor.state) : null;
        const mentionListVisible = !!document.querySelector('.items-list');
        if (mentionSuggestionState?.active || mentionListVisible) {
          return false;
        }

        event.preventDefault();
        event.stopPropagation();
        if (editor && !editor.isDestroyed) {
          editor.chain().focus().splitBlock().run();
        }
        return true;
      },
    },
    onUpdateCallback,
  });

  useEffect(() => {
    if (!editor) return;
    setTimeout(() => {
      if (!editor.isDestroyed) {
        previousMentionsIds.current = extractMentionsIdsFromEditor(editor);
      }
    }, 0);
  }, [editor, inputValue]);

  return (
    <div className="relative min-h-[156px] rounded-12 border border-system-white08 bg-system-white04 px-3 py-3 text-sm text-system-white leading-6 outline-none focus-within:border-[#508DFF]">
      <MentionContext.Provider
        value={{
          getPrefix: (id, label) => {
            const asset = assetMap.get(id) ?? activeShot.assets.find((item) => item.path === id);
            if (!asset) return null;
            return buildMentionPrefix({ ...asset, name: label || asset.name });
          },
        }}
      >
        <EditorContent editor={editor} className="w-full" />
      </MentionContext.Provider>
      {isEmpty && (
        <div className="pointer-events-none absolute top-3 left-3 select-none text-sm text-system-white36">
          {t('promptPlaceholder')}
        </div>
      )}
    </div>
  );
};

export const StoryShotPane = ({
  shots,
  activeShotIndex,
  activeShot,
  files,
  onPrev,
  onNext,
  onSelectShot,
  onUpdateShot,
  onAddAsset,
  onRemoveAsset,
  readOnly = false,
  className,
}: StoryShotPaneProps) => {
  const t = useTranslations('claw.storyDetail.shotPane');
  const totalShots = shots.length;
  const isFirst = activeShotIndex === 0;
  const isLast = activeShotIndex === totalShots - 1;
  const activeClawId = useStore((state) => state.activeClaw?.id);

  const [assetPreviewUrlMap, setAssetPreviewUrlMap] = useState<Record<string, string>>({});

  const previewSourceAssets = useMemo(() => {
    const fileAssets = files
      .filter((file) => shouldDisplayMediaFile(file))
      .map(fileItemToStoryAsset)
      .filter((asset) => !!asset.path);
    return dedupeStoryAssets([
      ...activeShot.assets,
      ...collectPromptReferencedAssets(activeShot.prompt, [...activeShot.assets, ...fileAssets]),
    ]);
  }, [activeShot.assets, activeShot.prompt, files]);

  // 时长输入用本地受控字符串：直接绑 activeShot.durationSec 时，清空会得到 Number('')===0 被拦下，
  // 下一帧旧值又被顶回 input，看起来像"删不掉、很难输入"。
  // 编辑期允许空串/中间态；只要解析成有效正整数就 onChange 同步回写（保证 spinner / 上下键即时生效），
  // blur / Enter 再 clamp 兜底，处理空值或越界。
  const [durationDraft, setDurationDraft] = useState<string>(() => String(activeShot.durationSec ?? ''));
  useEffect(() => {
    setDurationDraft(String(activeShot.durationSec ?? ''));
  }, [activeShot.id, activeShot.durationSec]);
  const handleDurationChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      setDurationDraft(raw);
      if (raw.trim() === '') return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      const rounded = Math.round(parsed);
      // 越界值不立刻 clamp 回写，避免打字过程中数字被拽回去；blur/Enter 时再统一 clamp。
      if (rounded < DURATION_MIN || rounded > DURATION_MAX) return;
      if (rounded !== activeShot.durationSec) {
        onUpdateShot(activeShot.id, { durationSec: rounded });
      }
    },
    [activeShot.id, activeShot.durationSec, onUpdateShot],
  );
  const commitDuration = useCallback(() => {
    const parsed = Number(durationDraft);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setDurationDraft(String(activeShot.durationSec ?? ''));
      return;
    }
    const clamped = Math.min(DURATION_MAX, Math.max(DURATION_MIN, Math.round(parsed)));
    if (clamped !== activeShot.durationSec) {
      onUpdateShot(activeShot.id, { durationSec: clamped });
    }
    setDurationDraft(String(clamped));
  }, [durationDraft, activeShot.id, activeShot.durationSec, onUpdateShot]);

  useEffect(() => {
    setAssetPreviewUrlMap({});
    if (!activeClawId) return;

    let cancelled = false;
    const visualAssets = previewSourceAssets.filter(
      (asset) => (asset.type === 'image' || asset.type === 'video') && asset.path && !asset.previewUrl,
    );
    if (!visualAssets.length) return;

    void (async () => {
      const previewResults = await Promise.all(
        visualAssets.map(async (asset) => {
          const path = asset.path.trim();
          const { data, error } = await getDownloadUrl({
            query: {
              claw_id: activeClawId,
              file_path: path,
            },
          });
          if (error || !data?.download_url) return null;
          return { path, url: data.download_url };
        }),
      );

      if (cancelled) return;
      const nextMap: Record<string, string> = {};
      previewResults.forEach((result) => {
        if (!result) return;
        nextMap[result.path] = result.url;
      });
      setAssetPreviewUrlMap(nextMap);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeClawId, previewSourceAssets]);

  const shotChips = useMemo(
    () =>
      shots.map((shot, idx) => ({
        id: shot.id,
        index: idx,
        active: idx === activeShotIndex,
      })),
    [shots, activeShotIndex],
  );

  return (
    <div className={cn('flex h-full min-h-0 flex-col bg-system-white04 light:bg-[rgba(18,24,38,0.04)]', className)}>
      <div className="flex items-center justify-between border-system-white08 border-b px-4 py-3">
        <span className="font-medium text-sm text-system-white">{t('title')}</span>
        <span className="text-system-white48 text-xs">
          {t('indicator', { current: activeShotIndex + 1, total: totalShots })}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 border-system-white08 border-b px-4 py-2">
        <button
          type="button"
          className="flex size-8 items-center justify-center rounded-full bg-system-white08 text-system-white transition hover:bg-system-white12 disabled:opacity-30"
          onClick={onPrev}
          disabled={isFirst}
          aria-label={t('previousShot')}
        >
          <ChevronLeft className="size-4" />
        </button>
        <div className="flex flex-1 flex-wrap items-center justify-center gap-1">
          {shotChips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={() => onSelectShot(chip.index)}
              className={cn(
                'rounded-full px-3 py-1 text-xs transition-colors',
                chip.active
                  ? 'bg-system-white light:bg-[#5765F2] light:text-white text-[#0A0D10] light:shadow-[0_1px_3px_rgba(87,101,242,0.24)]'
                  : 'bg-system-white08 text-system-white48 hover:bg-system-white12',
              )}
            >
              {chip.index + 1}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="flex size-8 items-center justify-center rounded-full bg-system-white08 text-system-white transition hover:bg-system-white12 disabled:opacity-30"
          onClick={onNext}
          disabled={isLast}
          aria-label={t('nextShot')}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <section className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-system-white48 text-xs">{t('references')}</span>
            <span className="text-system-white48 text-xs">
              {t('referenceCount', { count: activeShot.assets.length })}
            </span>
          </div>
          {activeShot.assets.length === 0 ? (
            <div className="rounded-12 border border-system-white08 border-dashed px-3 py-4 text-center text-system-white48 text-xs">
              {t('emptyReferences')}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {activeShot.assets.map((asset) => (
                  <div key={asset.path} className="flex w-20 flex-col gap-1">
                    <ShotAssetChip
                      asset={{
                        ...asset,
                        previewUrl: asset.previewUrl || assetPreviewUrlMap[asset.path],
                      }}
                      size="md"
                    />
                    <span
                      className="line-clamp-2 break-all text-[11px] text-system-white80 leading-tight"
                      title={asset.path || asset.name}
                    >
                      {asset.name || asset.path}
                    </span>
                  </div>
                ))}
              </div>
              {readOnly ? null : <div className="mt-2 text-[11px] text-system-white48">{t('removeHint')}</div>}
            </>
          )}
        </section>

        <section className="mb-4">
          <div className="mb-2 text-system-white48 text-xs">{t('prompt')}</div>
          <StoryPromptEditor
            activeShot={activeShot}
            files={files}
            assetPreviewUrlMap={assetPreviewUrlMap}
            onUpdatePrompt={(prompt) => onUpdateShot(activeShot.id, { prompt })}
            onAddAsset={(asset) => onAddAsset(activeShot.id, asset)}
            onRemoveAsset={(assetPath) => onRemoveAsset(activeShot.id, assetPath)}
            readOnly={readOnly}
          />
        </section>

        <section>
          <div className="mb-2 text-system-white48 text-xs">{t('duration')}</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={DURATION_MIN}
              max={DURATION_MAX}
              step={1}
              value={durationDraft}
              onChange={handleDurationChange}
              onBlur={commitDuration}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitDuration();
                  event.currentTarget.blur();
                }
              }}
              readOnly={readOnly}
              disabled={readOnly}
              className="w-24 rounded-12 border border-system-white08 bg-system-white04 px-3 py-2 text-center text-sm text-system-white outline-none focus:border-[#508DFF] disabled:cursor-not-allowed disabled:opacity-70"
            />
            <span className="text-system-white48 text-xs">s</span>
          </div>
        </section>
      </div>
    </div>
  );
};
